import type { ServiceStatusVariant } from "kontrolEdge/helpers/serviceStatusTag";
import type {
  CarrierAccessEndpoint,
  CarrierAccessLeg,
} from "../carrierAccess.types";

/**
 * Carrier Access detail — presentation of the derived legs (CTX-653).
 *
 * Pure helpers, kept out of the view so the wording that carries a meaning
 * (an idle standby is not a warning) has one source and a test.
 */

const LEG_STATUS_TAGS: Record<
  NonNullable<CarrierAccessLeg["status"]>,
  { label: string; variant: ServiceStatusVariant }
> = {
  active: { label: "Active", variant: "success" },
  standby: { label: "Standby", variant: "default" },
  up: { label: "Up", variant: "success" },
  warning: { label: "Warning", variant: "warning" },
  down: { label: "Down", variant: "error" },
};

/**
 * The leg's status as a tag. Standby is neutral, not amber: an idle standby
 * is the access working as sold. Warning is reserved for "no carrier of this
 * access carries traffic".
 */
export const legStatusTag = (
  status: CarrierAccessLeg["status"]
): { label: string; variant: ServiceStatusVariant } =>
  (status && LEG_STATUS_TAGS[status]) || {
    label: "No data",
    variant: "default",
  };

/**
 * Whether the leg has telemetry to plot. A leg with no reading ("No data")
 * would only draw an empty card.
 */
export const legHasReading = (leg: CarrierAccessLeg): boolean =>
  leg.status !== null && leg.status !== undefined;

/**
 * Route of an endpoint's own detail view. Both views read their id from the
 * query string, so they load on a direct link.
 */
export const endpointDetailPath = (endpoint: CarrierAccessEndpoint): string =>
  endpoint.type === "port"
    ? `/kontrol/port-details?portId=${endpoint.id}`
    : `/kontrol/extender-pop-details?extenderPopId=${endpoint.id}`;
